import type * as React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { ApprovalPortalScreen } from "@/features/approval/ApprovalPortalScreen";
import { AdminLoginScreen } from "@/features/auth/AdminLoginScreen";
import { useAuth } from "@/features/auth/auth-context";
import { LoginScreen } from "@/features/auth/LoginScreen";
import {
  ProtectedRoute,
  RequireAnyCapability,
  RequireCapability,
} from "@/features/auth/ProtectedRoute";
import { ChallanDetailScreen } from "@/features/challans/ChallanDetailScreen";
import { ChallanEditorScreen } from "@/features/challans/ChallanEditorScreen";
import { ChallanListScreen } from "@/features/challans/ChallanListScreen";
import { CreditNoteDetailScreen } from "@/features/credit-notes/CreditNoteDetailScreen";
import { CreditNoteEditorScreen } from "@/features/credit-notes/CreditNoteEditorScreen";
import { CreditNoteListScreen } from "@/features/credit-notes/CreditNoteListScreen";
import { CustomerDetailScreen } from "@/features/customers/CustomerDetailScreen";
import { CustomerEditorScreen } from "@/features/customers/CustomerEditorScreen";
import { CustomerListScreen } from "@/features/customers/CustomerListScreen";
import { HomeScreen } from "@/features/home/HomeScreen";
import { InvoiceDetailScreen } from "@/features/invoices/InvoiceDetailScreen";
import { InvoiceEditorScreen } from "@/features/invoices/InvoiceEditorScreen";
import { InvoiceListScreen } from "@/features/invoices/InvoiceListScreen";
import { ItemEditorScreen } from "@/features/items/ItemEditorScreen";
import { ItemListScreen } from "@/features/items/ItemListScreen";
import { MailboxScreen } from "@/features/mail/MailboxScreen";
import { MessengerScreen } from "@/features/messages/MessengerScreen";
import { NotificationsScreen } from "@/features/notifications/NotificationsScreen";
import { QuotationDetailScreen } from "@/features/quotations/QuotationDetailScreen";
import { QuotationEditorScreen } from "@/features/quotations/QuotationEditorScreen";
import { QuotationListScreen } from "@/features/quotations/QuotationListScreen";
import { ReportsScreen } from "@/features/reports/ReportsScreen";
import { AuditScreen } from "@/features/audit/AuditScreen";
import { CountersScreen } from "@/features/settings/CountersScreen";
import { FeaturesScreen } from "@/features/settings/FeaturesScreen";
import { SettingsLayout } from "@/features/settings/SettingsLayout";
import { firstSettingsSection } from "@/features/settings/settings-sections";
import { SettingsScreen } from "@/features/settings/SettingsScreen";
import { UsersScreen } from "@/features/users/UsersScreen";
/*
 * `NotBuiltYetScreen` IS NO LONGER IMPORTED — every declared route renders a
 * real screen, and as of 2026-08-16 §17.3's credit notes are among them. The
 * component itself is kept: it is the honest thing to mount when a route must
 * exist before its screen does.
 */
import { landingPath } from "@/routes/landing";
import { patterns, routes } from "@/routes/paths";

/**
 * What `/` renders — §11.2's home screen, or a redirect to the screen this
 * person actually came for.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * NOT EVERY SIGNED-IN PERSON BELONGS ON THE MAKING SCREEN. The ACCOUNTANT holds
 * no create capability at all, so §11.2's four action tiles collapse to one
 * ("Customers") and the largest, most deliberate element in the app becomes a
 * gap. She lands on REPORTS instead — §17.4's register and GSTR-1 exports, the
 * reason she has a login.
 *
 * THE CHOICE IS MADE BY `landingPath`, WHICH READS CAPABILITIES AND NOTHING
 * ELSE (§2.1). No role string appears here or in that file. For OWNER and ADMIN
 * it returns `routes.home` and this component renders `<HomeScreen />` exactly
 * as the plain route used to — no redirect, no extra frame, no changed URL.
 *
 * A REDIRECT RATHER THAN "RENDER REPORTS AT `/`". The URL has to become
 * `/reports`, or the sidebar highlights nothing, the header title says "Home"
 * over a reports screen, and a bookmark of the screen she uses every month
 * points at a path that means something different for the person beside her.
 *
 * `replace` SO THE BACK BUTTON LEAVES THE APP rather than returning to `/` and
 * bouncing forward again — a redirect left in the history stack is a back
 * button that appears not to work.
 *
 * IT CANNOT LOOP. `landingPath` returns a path only when the caller holds the
 * capability guarding it, and its fallback is `/` itself, which renders rather
 * than redirects. See `routes/landing.ts`.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function LandingRoute(): React.JSX.Element {
  const { can } = useAuth();
  const destination = landingPath(can);

  if (destination === routes.home) return <HomeScreen />;
  return <Navigate to={destination} replace />;
}

/**
 * What `/settings` renders — §7.3's company form, or the first section of the
 * settings area this person can actually open.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE SAME SHAPE AS `LandingRoute` ABOVE, AND FOR THE SAME REASON. The area now
 * has ONE door in the rail, and that door is offered to anyone holding
 * `settings.write`, `user.manage` OR `audit.view` (§2.1's Administration row —
 * three capabilities that coincide only because ADMIN holds all three today). If
 * `/settings` still meant "the company form and nothing else", the row would
 * bounce a future `user.manage`-only role straight Home — the door shown, and
 * shut. So this asks the question the reader meant: take me to my settings.
 *
 * IT WIDENS NOTHING. `settings.write` is still what renders `SettingsScreen`,
 * and it is still the only capability that reaches the GSTIN, the PAN and the
 * bank details; a reader without it never renders that component at all, here or
 * anywhere. Every other section keeps its own `RequireCapability` below.
 *
 * IT CANNOT LOOP. `firstSettingsSection` answers only with sections the caller
 * holds, and it can answer `/settings` ONLY for `settings.write` — the case
 * handled above by rendering rather than redirecting. `null` means the area is
 * not theirs at all, which is Home, exactly as `RequireCapability` would have
 * said. §6.1's counters are deliberately not a possible answer: see
 * `SETTINGS_AREA_CAPABILITIES` in `features/settings/settings-sections.ts`.
 * ═════════════════════════════════════════════════════════════════════════════
 */
function SettingsIndexRoute(): React.JSX.Element {
  const { can } = useAuth();

  if (can("settings.write")) return <SettingsScreen />;

  const first = firstSettingsSection(can);
  if (first === null || first === routes.settings) return <Navigate to={routes.home} replace />;
  return <Navigate to={first} replace />;
}

/**
 * The route tree.
 *
 * TWO GATES, ANSWERING DIFFERENT QUESTIONS (§2.1):
 *
 *   `ProtectedRoute`     — "are you signed in?"  → redirect to login
 *   `RequireCapability`  — "may you do this?"    → redirect Home
 *
 * Sending a signed-in user who lacks a capability to the LOGIN screen would loop
 * him forever: signing in again cannot grant a capability his role does not
 * hold. The two gates are not interchangeable.
 *
 * EVERY GATE NAMES A CAPABILITY, NEVER A ROLE, and it is the same capability the
 * sidebar filters on and the same one `requireCap` enforces on the server — one
 * `can()` from `packages/types`, so the three cannot drift apart.
 *
 * The capability on each route MATCHES the one on its sidebar item. If they ever
 * disagree the item is either invisible-but-reachable or visible-but-forbidden,
 * and both are worse than either being consistently wrong.
 */
export function AppRoutes(): React.JSX.Element {
  return (
    <Routes>
      <Route path={routes.login} element={<LoginScreen />} />

      {/*
        * UNLISTED. Nothing links here — you reach it by typing the path. That
        * keeps scanners away and stops the owner wandering onto a screen asking
        * for a password he does not have. It is NOT what secures the account:
        * the path is in the public bundle. The real defences are the server's
        * rate limit, the lockout and bcrypt — see `admin-login-form.tsx`.
        */}
      <Route path={routes.adminLogin} element={<AdminLoginScreen />} />

      {/*
        * ══════════════════════════════════════════════════════════════════
        * §17.1's CUSTOMER APPROVAL PORTAL. THE ONLY ROUTE IN THIS TREE THAT IS
        * OUTSIDE BOTH `ProtectedRoute` AND `AppShell`, and both exclusions are
        * requirements rather than tidiness.
        *
        * OUTSIDE `ProtectedRoute` because the reader has no account and never
        * will (CLAUDE.md: "No customer portal with logins — signed magic links
        * instead"). Inside it, every customer opening a WhatsApp link would be
        * bounced to a sign-in screen for a system they have nothing to do with.
        *
        * OUTSIDE `AppShell` because the shell is the BUSINESS's furniture — the
        * sidebar, the search, the notification bell, the profile menu — and it
        * calls hooks that assume a session. A customer must be shown one
        * quotation and the two buttons, and nothing else that exists here.
        *
        * BESIDE THE LOGIN ROUTES rather than at the bottom, so the three
        * unauthenticated entry points to this app read together in one place.
        * ══════════════════════════════════════════════════════════════════
        */}
      <Route path={routes.approvalPortal} element={<ApprovalPortalScreen />} />

      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          {/* §11.2's home for whoever it is for; a redirect for whoever it is
            * not. See `LandingRoute` above — the decision is a capability, not
            * a role. */}
          <Route path={routes.home} element={<LandingRoute />} />

          {/*
            * ORDER MATTERS, AND SO DOES COMPLETENESS.
            *
            * `invoices/new` and `invoices/record` are STATIC and must be
            * declared before `invoices/:id`, or React Router matches them as an
            * id of "new" and the detail screen asks the API for invoice "new".
            *
            * More importantly: every path the app LINKS to must be declared
            * here. Until now only the list routes were, so `/invoices/new` and
            * `/invoices/:id` fell through to the catch-all at the bottom and
            * silently redirected Home — which is what every row of the register
            * and all four Quick Create items did. A dead link that bounces you
            * to the dashboard reads as the app ignoring the tap, and it is
            * indistinguishable from a bug in the button.
            *
            * AND A ROUTE THAT EXISTS IS STILL NOT A ROUTE THE USER CAN REACH —
            * see the counters block immediately below, which resolved perfectly
            * and redirected the owner Home anyway.
            */}
          {/*
            * §6.1's COUNTER OVERRIDE, ON ITS OWN GATE — `invoice.set_counter`,
            * and deliberately NOT inside the `invoice.view` group below.
            *
            * IT USED TO BE `<Navigate to={routes.settings} replace />`, and that
            * one line quietly undid §6.1. `/settings` is guarded by
            * `settings.write`, which is ADMIN-only; the OWNER holds
            * `invoice.set_counter` and not `settings.write`
            * (`permissions.ts` — COUNTER_MANAGEMENT vs ADMIN_ONLY). So the
            * chain for the one person this screen is FOR ran: `invoice.view`
            * passes → redirect to `/settings` → `settings.write` fails →
            * `<Navigate to="/">`. He landed on the dashboard with no message,
            * which reads as the app ignoring the tap. `paths.ts` had described
            * the correct design all along ("ITS OWN ROUTE, GATED ON
            * `invoice.set_counter`"); this table still implemented the one it
            * replaced. Loosening `/settings` would have been the wrong repair —
            * it guards the GSTIN, the PAN and the bank details.
            *
            * Declared before `invoices/:id` for the reader; React Router ranks a
            * static segment above a dynamic one regardless, and a pathless
            * capability wrapper adds nothing to that score.
            */}
          {/*
            * §6.1's counter override, now at `/settings/numbers` — the reasoning
            * for the move is on `routes.invoiceNumbers`.
            *
            * THE GATE IS UNTOUCHED AND STAYS `invoice.set_counter`. That is the
            * whole point of the move being safe: `SettingsLayout` is ungated, so
            * living under `/settings/` does not put this behind `settings.write`,
            * which the owner does not hold and §6.1 spends a page insisting must
            * not become the gate here.
            *
            * The frame is PATHLESS so following the menu item does not drop the
            * reader out of the area with no way back to the other four.
            */}
          <Route element={<SettingsLayout />}>
            <Route element={<RequireCapability capability="invoice.set_counter" />}>
              <Route path={patterns.invoiceNumbers} element={<CountersScreen />} />
            </Route>
          </Route>

          <Route element={<RequireCapability capability="invoice.view" />}>
            <Route path={routes.invoices} element={<InvoiceListScreen />} />
            <Route path={patterns.invoice} element={<InvoiceDetailScreen />} />
          </Route>

          <Route element={<RequireCapability capability="invoice.create" />}>
            <Route path={patterns.invoiceNew} element={<InvoiceEditorScreen />} />
            {/*
              * §6.1 — the same editor in "record" mode: he supplies the number
              * and the date off the paper bill, and the counter moves past it.
              */}
            <Route
              path={patterns.invoiceRecordManual}
              element={<InvoiceEditorScreen mode="record" />}
            />
          </Route>

          {/*
            * `quotationNew` and `quotationEdit` BEFORE `quotation`, and both
            * under `quotation.create` rather than `quotation.view` — writing an
            * offer is a create, and revising one goes through the same `PUT`
            * that `quotation.create` authorises (§2.1: there is no separate
            * `quotation.edit`, because a quotation is not a tax document and
            * revising an unaccepted offer is the same act as writing it).
            *
            * React Router ranks a static segment above a dynamic one regardless
            * of declaration order, so `quotations/new` cannot be swallowed by
            * `quotations/:id` — the order here is for the reader.
            */}
          <Route element={<RequireCapability capability="quotation.create" />}>
            <Route path={patterns.quotationNew} element={<QuotationEditorScreen />} />
            <Route path={patterns.quotationEdit} element={<QuotationEditorScreen />} />
          </Route>
          <Route element={<RequireCapability capability="quotation.view" />}>
            <Route path={routes.quotations} element={<QuotationListScreen />} />
            <Route path={patterns.quotation} element={<QuotationDetailScreen />} />
          </Route>

          {/*
            * §17.7. `challanNew` and `challanEdit` under `challan.create`, the
            * detail and the register under `challan.view` — the same split the
            * quotations above use, and for the same reason: revising a movement
            * that is still open is the same act as recording it, so §2.1 has no
            * separate `challan.edit` for the SCREEN to hold. The server still
            * gates the `PUT` on `challan.edit` AND on the challan's own status.
            *
            * ONLY THE REGISTER WAS WIRED UNTIL 2026-08-16, so "New challan" fell
            * through every route and landed on the home screen — a dead button on
            * a shipped feature. React Router ranks a static segment above a
            * dynamic one regardless of order, so `challans/new` cannot be
            * swallowed by `challans/:id`; the order here is for the reader.
            */}
          <Route element={<RequireCapability capability="challan.create" />}>
            <Route path={patterns.challanNew} element={<ChallanEditorScreen />} />
            <Route path={patterns.challanEdit} element={<ChallanEditorScreen />} />
          </Route>
          <Route element={<RequireCapability capability="challan.view" />}>
            <Route path={routes.challans} element={<ChallanListScreen />} />
            <Route path={patterns.challan} element={<ChallanDetailScreen />} />
          </Route>

          {/*
            * §17.3 — CREDIT NOTES. Three paths, and the SHAPE of them is the
            * feature's first rule rather than a routing preference.
            *
            * THE EDITOR LIVES UNDER `invoices/:id`, not under `credit-notes`.
            * A credit note is raised FROM the bill it corrects (§34 requires the
            * link), so there is deliberately no `credit-notes/new` to mount:
            * a path that cannot be reached without naming an invoice cannot be
            * reached with the wrong one blank. It has MORE SEGMENTS than
            * `invoices/:id`, so React Router ranks it higher and the bill's
            * detail screen cannot swallow it — the same shape, and the same
            * reason, as `customers/:id/statement`.
            *
            * TWO CAPABILITIES, MATCHING THE SIDEBAR AND THE SERVER. The editor
            * is `creditnote.create`; the register and the detail screen are
            * `creditnote.view`, the shared read row the ACCOUNTANT holds — she
            * files the CDNR section of GSTR-1, so a credit note she cannot see
            * is a return she cannot complete. There is no `creditnote.edit` for
            * any route to name, deliberately (§2.1).
            *
            * THE `credit_notes` FLAG IS NOT — AND CANNOT BE — CHECKED HERE. A
            * route guard cannot see a flag: `GET /flags` needs `settings.write`,
            * which the OWNER does not hold. The API answers 404 while the module
            * is off (§18.3) and both screens render "not switched on". A
            * capability gate and a feature flag are different mechanisms, and
            * these routes carry only the one they can enforce.
            */}
          <Route element={<RequireCapability capability="creditnote.create" />}>
            <Route path={patterns.creditNoteFor} element={<CreditNoteEditorScreen />} />
          </Route>
          <Route element={<RequireCapability capability="creditnote.view" />}>
            <Route path={patterns.creditNotes} element={<CreditNoteListScreen />} />
            <Route path={patterns.creditNote} element={<CreditNoteDetailScreen />} />
          </Route>

          <Route element={<RequireCapability capability="customer.view" />}>
            <Route path={routes.customers} element={<CustomerListScreen />} />
            <Route path={patterns.customer} element={<CustomerEditorScreen />} />
          </Route>
          {/*
            * §17.5's statement — `payment.view`, NOT `customer.view`, and the
            * difference is the whole reason it is not a section on the editor.
            * "What is their address" and "what do they owe me" are different
            * questions with different answers about who may ask them: the
            * ACCOUNTANT holds `payment.view` and no editing capability at all.
            *
            * The screen is behind the `payments` FLAG as well, which no route
            * guard can see — the API answers 404 while the module is off
            * (§18.3) and the screen renders "not switched on". A capability
            * gate and a feature flag are different mechanisms and this route
            * carries only the one it can enforce.
            */}
          <Route element={<RequireCapability capability="payment.view" />}>
            <Route path={patterns.customerStatement} element={<CustomerDetailScreen />} />
          </Route>
          <Route element={<RequireCapability capability="customer.create" />}>
            <Route
              path={patterns.customerNew}
              element={<CustomerEditorScreen />}
            />
          </Route>

          <Route element={<RequireCapability capability="item.view" />}>
            <Route path={routes.items} element={<ItemListScreen />} />
            <Route path={patterns.item} element={<ItemEditorScreen />} />
          </Route>
          <Route element={<RequireCapability capability="item.create" />}>
            <Route path={patterns.itemNew} element={<ItemEditorScreen />} />
          </Route>

          {/*
            * §17.7's notification hub — what the bell in the header opens.
            *
            * `notification.view`, which EVERY signed-in role holds, including the
            * ACCOUNTANT: the flagship alert is addressed to whoever can record an
            * e-way bill number, and an inbox its recipient cannot open is not an
            * inbox. That makes this the one gate here that hides nothing today —
            * and it is still written, because a capability is how the question is
            * asked in this codebase (§2.1) and a route with no gate is a route
            * nobody rechecks when a role is added.
            *
            * THE `notifications` FLAG IS NOT CHECKED HERE AND CANNOT BE. A route
            * guard cannot see a flag — `GET /flags` needs `settings.write`, which
            * the OWNER does not hold. The API answers 404 while the module is off
            * (§18.3) and the screen renders "not switched on"; the bell that
            * leads here renders nothing at all. Capability gates and feature flags
            * are different mechanisms, and this route carries only the one it can
            * enforce — the same shape as credit notes and the statement above.
            */}
          <Route element={<RequireCapability capability="notification.view" />}>
            <Route path={patterns.notifications} element={<NotificationsScreen />} />
          </Route>

          {/*
            * ═══════════════════════════════════════════════════════════════════
            * THE MESSENGER (2026-08-19) — THREE PATHS, ONE SCREEN, THREE
            * DIFFERENT GATES.
            *
            *   `/messages`          the LIST: the people, then the group below
            *                        them. `message.view` OR `message.direct`.
            *   `/messages/group`    the shared room.        `message.view`
            *   `/messages/:userId`  one private conversation. `message.direct`
            *
            * WHAT WAS DELETED: `/messages/direct` and `/messages/direct/:userId`.
            * Not aliased and not redirected — the private conversations are rows
            * on `/messages` now. A leftover bookmark hits `messages/:userId` with
            * a segment that is not digits, and `MessengerScreen` sends it Home
            * exactly as the catch-all sends every other unrecognised path.
            *
            * `messages/group` IS DECLARED FIRST, AND IT DOES NOT NEED TO BE.
            * React Router ranks a static segment above a dynamic one whatever the
            * order, and `parsePeerId` accepts digits only, so "group" could never
            * be read as a user id — two independent reasons this is unambiguous.
            * It is declared first anyway so that a reader scanning this table
            * does not have to know either of them.
            *
            * THE LIST TAKES `RequireAnyCapability` AND THAT IS THE POINT OF THE
            * SCREEN. §2.1 keeps `message.direct` separate from `message.view`
            * precisely so an outside professional can be confined to the room
            * where everything is on the record and no side channel exists. Hold
            * `message.view` alone and the list is one row — the group. Hold
            * `message.direct` alone and it is only the people. Hold neither and
            * both this guard and the header control give you nothing. Requiring
            * BOTH here would hide the whole messenger from either of those
            * people; requiring `message.view` alone would hide it from the second.
            *
            * EACH CONVERSATION STILL CARRIES ITS OWN GATE. Reaching the list is
            * not reaching a room: `/messages/group` asks for `message.view` on its
            * own terms, `/messages/:userId` for `message.direct`, and the API
            * asks again with `requireCap` reading the same map (§2.1).
            *
            * THE `messages` FLAG IS NOT CHECKED HERE AND CANNOT BE. A route guard
            * cannot see a flag — `GET /flags` needs `settings.write`, which the
            * OWNER does not hold. The API answers 404 while the module is off
            * (§18.2) and the screen renders "not switched on"; the header control
            * that leads here renders nothing at all. Same shape as the hub above,
            * and as credit notes and the statement.
            * ═══════════════════════════════════════════════════════════════════
            */}
          <Route
            element={
              <RequireAnyCapability capabilities={["message.view", "message.direct"]} />
            }
          >
            <Route path={patterns.messages} element={<MessengerScreen />} />
          </Route>
          <Route element={<RequireCapability capability="message.view" />}>
            <Route path={patterns.messagesGroup} element={<MessengerScreen />} />
          </Route>
          <Route element={<RequireCapability capability="message.direct" />}>
            <Route path={patterns.messagesWith} element={<MessengerScreen />} />
          </Route>

          {/*
            * §17A.1's mailbox — what the rail's Mail row opens.
            *
            * `mail.view`, which is a NARROWER gate than any of the three above:
            * the OWNER and the ADMIN hold it and the ACCOUNTANT does not
            * (`permissions.ts` — READ_MAIL). So unlike the hub and the
            * conversation, this gate hides something today, and the rail row is
            * filtered on the SAME capability — a row that led to a redirect Home
            * would be the app lying about where it goes.
            *
            * IT IS `mail.view` AND NOT `mail.send`, because reading what went
            * out and putting something new in a customer's inbox are separate
            * grants (§2.1). A future clerk with the send and not the read walks
            * past this door; a future reader with the read and not the send opens
            * it and finds nothing to press, which is correct — nothing is sent
            * from this screen at all.
            *
            * THE `mail` FLAG IS NOT CHECKED HERE AND CANNOT BE. A route guard
            * cannot see a flag — `GET /flags` needs `settings.write`, which the
            * OWNER does not hold. The API answers 404 while the module is off
            * (§18.2) and the screen renders "not switched on". Same shape as
            * credit notes, the statement, the hub and the conversation.
            */}
          <Route element={<RequireCapability capability="mail.view" />}>
            <Route path={patterns.mail} element={<MailboxScreen />} />
          </Route>

          <Route element={<RequireCapability capability="report.view" />}>
            <Route path={routes.reports} element={<ReportsScreen />} />
          </Route>

          {/*
            * THE SETTINGS AREA — ONE FRAME, FOUR PATHS, FOUR SEPARATE GATES.
            *
            * ═══════════════════════════════════════════════════════════════════
            * `SettingsLayout` IS PATHLESS AND UNGATED, AND BOTH HALVES MATTER.
            *
            * PATHLESS, so every URL below is exactly what it was — `/settings`,
            * `/settings/features`, `/settings/users`, `/settings/audit`. The rail
            * offers one row instead of four now, and the sections are a
            * navigation menu across the top of the frame; a bookmark, a back
            * button and a middle click are unaffected because these are still
            * routes and the menu items are still links to them.
            *
            * UNGATED, because the frame is not a screen. Gating it on
            * `settings.write` would put every section behind the company form's
            * capability — the exact collapse the block below has warned about
            * since §6.1's counter override paid for it. The frame only decides
            * what to OFFER; each route below still decides who may ENTER, and
            * they are unchanged.
            * ═══════════════════════════════════════════════════════════════════
            */}
          <Route element={<SettingsLayout />}>
            {/*
              * `/settings` ITSELF IS NOT INSIDE THE `settings.write` WRAPPER ANY
              * MORE, and that is not a loosening. `SettingsIndexRoute` renders
              * the company form ONLY for `settings.write` and otherwise redirects
              * to the first section the reader can open — see its note above. The
              * capability that reaches the GSTIN, the PAN and the bank details is
              * the same one it always was.
              */}
            <Route path={routes.settings} element={<SettingsIndexRoute />} />

            {/*
              * §18.1's flag screen keeps `settings.write` because that IS the
              * capability its own API enforces: the whole `/flags` router sits
              * behind it, the READ included, since the list names every
              * unreleased module in the build. So this is not the neighbouring-
              * capability mistake the block below warns about — the route names
              * the gate the server will apply.
              *
              * `settings/features` is a static segment and `settings` carries no
              * splat, so the company form cannot swallow it; the order is for the
              * reader.
              */}
            <Route element={<RequireCapability capability="settings.write" />}>
              <Route path={patterns.features} element={<FeaturesScreen />} />
            </Route>

            {/*
              * §2, §13 — THE TWO ADMINISTRATION SURFACES §2.1 NAMED, each on its
              * OWN capability and neither on `settings.write`.
              *
              * ═════════════════════════════════════════════════════════════════
              * THEY SIT UNDER `/settings/`, THEY NOW SHARE A FRAME WITH THE
              * COMPANY FORM, AND THEY STILL DO NOT SHARE ITS GATE. That
              * distinction is the one §6.1's counter override paid for.
              * `settings.write`, `user.manage` and `audit.view` are three
              * separate rows of §2.1's Administration table; they resolve to the
              * same person today only because ADMIN holds all three. Nesting
              * these under the `settings.write` wrapper would bind "may I create
              * an account" to "may I change the bank details", so a future role
              * granted `user.manage` alone would pass its own gate and then be
              * bounced Home by somebody else's — which is precisely how
              * `/invoices/numbers` became unreachable for the one person it was
              * built for. The shared frame is a `<nav>` and an `<Outlet/>`; it
              * asks for nothing, which is why it can wrap all four.
              *
              * NEITHER IS BEHIND A FEATURE FLAG, on the client or the server.
              * §18.3 forbids flagging authentication and names the audit write
              * among the things nothing may be flaggable that changes; a switch
              * that could 404 either of these could stop the last administrator
              * fixing a lockout, or make a legally-retained log unreadable. So a
              * 404 from either API is a real 404 and the screens do not render
              * `FeatureOff`.
              * ═════════════════════════════════════════════════════════════════
              *
              * Both paths are STATIC segments, and `settings` carries no splat,
              * so neither can be swallowed by the company form above.
              */}
            <Route element={<RequireCapability capability="user.manage" />}>
              <Route path={patterns.users} element={<UsersScreen />} />
            </Route>
            <Route element={<RequireCapability capability="audit.view" />}>
              <Route path={patterns.audit} element={<AuditScreen />} />
            </Route>
          </Route>

        </Route>
      </Route>

      {/* An unrecognised path goes Home rather than to a 404 the owner cannot
        * act on. A wrong URL is nearly always a stale bookmark or a mistyped
        * deep link, and Home is where every path in this app begins. */}
      <Route path="*" element={<Navigate to={routes.home} replace />} />
    </Routes>
  );
}
